import React, { useState, useMemo } from 'react';
import jsonData from './data.json';
import './DataViewer.css';

const ITEMS_PER_PAGE = 10;

// Get unique values for filters, filtering out any empty/falsy values
const uniqueTipos = ['all', ...new Set(jsonData.data.map(item => item.tipo).filter(Boolean))];
const uniqueEstados = ['all', ...new Set(jsonData.data.map(item => item.direccion.estado).filter(Boolean))];
const uniqueEstatus = ['all', ...new Set(jsonData.data.map(item => item.estatus).filter(Boolean))];

const DataViewer = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const [estadoFilter, setEstadoFilter] = useState('all');
  const [estatusFilter, setEstatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const filteredData = useMemo(() => {
    return jsonData.data.filter(item => {
      const searchTermLower = searchTerm.toLowerCase();
      const itemCadena = item.cadena ? item.cadena.toLowerCase() : '';
      const itemNombre = item.nombre ? item.nombre.toLowerCase() : '';
      const itemDireccion = item.direccion ? 
        Object.values(item.direccion).join(' ').toLowerCase() : '';

      const matchesSearch = itemCadena.includes(searchTermLower) ||
                            itemNombre.includes(searchTermLower) ||
                            itemDireccion.includes(searchTermLower);

      const matchesFilter = filter === 'all' || item.tipo === filter;
      const matchesEstado = estadoFilter === 'all' || item.direccion.estado === estadoFilter;
      const matchesEstatus = estatusFilter === 'all' || item.estatus === estatusFilter;

      return matchesSearch && matchesFilter && matchesEstado && matchesEstatus;
    });
  }, [searchTerm, filter, estadoFilter, estatusFilter]);

  const totalPages = Math.ceil(filteredData.length / ITEMS_PER_PAGE);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredData.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [currentPage, filteredData]);

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const resetFiltersAndSearch = () => {
      setSearchTerm('');
      setFilter('all');
      setEstadoFilter('all');
      setEstatusFilter('all');
      setCurrentPage(1);
  }

  return (
    <div className="data-viewer">
      <header className="app-header">
        <h1>Sucursales Depósitos en Efectivo</h1>
      </header>
      <div className="controls-container">
        <div className="controls">
          <input
            type="text"
            placeholder="Buscar..."
            value={searchTerm}
            onChange={e => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
          <select value={filter} onChange={e => { setFilter(e.target.value); setCurrentPage(1); }}>
            {uniqueTipos.map(tipo => (
              <option key={tipo} value={tipo}>{tipo === 'all' ? 'Todos los tipos' : tipo}</option>
            ))}
          </select>
          <select value={estadoFilter} onChange={e => { setEstadoFilter(e.target.value); setCurrentPage(1); }}>
            {uniqueEstados.map(estado => (
              <option key={estado} value={estado}>{estado === 'all' ? 'Todos los estados' : estado}</option>
            ))}
          </select>
          <select value={estatusFilter} onChange={e => { setEstatusFilter(e.target.value); setCurrentPage(1); }}>
            {uniqueEstatus.map(estatus => (
              <option key={estatus} value={estatus}>{estatus === 'all' ? 'Cualquier estatus' : estatus}</option>
            ))}
          </select>
          <button onClick={resetFiltersAndSearch} className="reset-btn">Limpiar</button>
        </div>
      </div>
      
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Nombre</th>
              <th>Cadena</th>
              <th>Tipo</th>
              <th>Estatus</th>
              <th>Dirección</th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((item, index) => (
              <tr key={index}>
                <td>{item.nombre}</td>
                <td>{item.cadena}</td>
                <td><span className={`type ${item.tipo}`}>{item.tipo}</span></td>
                <td><span className={`status ${item.estatus === 'Abierta' ? 'open' : 'closed'}`}>{item.estatus}</span></td>
                <td>{`${item.direccion.calle} ${item.direccion.num_ext}, ${item.direccion.colonia}, ${item.direccion.ciudad}, ${item.direccion.estado}, C.P. ${item.direccion.codigo_postal}`}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="pagination">
        <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
          Anterior
        </button>
        <div className="page-info">
          Página <strong>{currentPage}</strong> de <strong>{totalPages}</strong>
        </div>
        <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>
          Siguiente
        </button>
      </div>

      <div className="help-container">
        <span onClick={() => setIsModalOpen(true)} className="help-link">
          Términos y Condiciones
        </span>
      </div>

      {isModalOpen && (
        <div className="modal-overlay" onClick={() => setIsModalOpen(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <button onClick={() => setIsModalOpen(false)} className="modal-close-btn">&times;</button>
            <div className="modal-body">
              <section className="modal-section">
                <h3 className="modal-subtitle">Términos y Condiciones</h3>
                <p className="modal-text">
                  Los establecimientos mostrados forman parte de la red de comisionistas administrados por PAGO EXPRESS SERVICIOS PREPAGADOS S.A de C.V (PESPAY) para la recepción de efectivo con destino a cuentas de depósito, actuando a nombre y cuenta de Financiera Sustentable de México, S. A. de C. V., S. F. P. (FINSUS)
                </p>
              </section>

              <section className="modal-section">
                <h3 className="modal-subtitle">Condiciones y límites:</h3>
                <ul className="modal-list">
                  <li>Monto máximo por operación de $10,000.00 MXN hasta $15,000.00 MXN conforme al límite establecido por cada comercio.</li>
                  <li>Límite acumulado diario por cliente: $30,000.00 MXN</li>
                  <li>Límite acumulado mensual por cliente: $80,000.00 MXN</li>
                </ul>
              </section>

              <section className="modal-section">
                <p className="modal-text">
                  Los depósitos se realizan mediante la verificación de un número de referencia único y fijo por cliente que se encuentra vinculado a su cuenta de depósito.
                </p>
                <p className="modal-text">
                  Las operaciones están sujetas a validaciones en tiempo real, disponibilidad de sistema y cumplimiento de disposiciones regulatorias (PLD/FT).
                </p>
                <p className="modal-text">
                  Cada establecimiento cobra una comisión por depósito que se realice, FINSUS no realiza ningún cobro adicional a sus usuarios.
                </p>
                <p className="modal-text">
                  Los abonos se ven reflejados en tiempo real en la cuenta de depósito del beneficiario.
                </p>
              </section>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataViewer;
